import { useState } from 'react';
import { Box, Typography, Tabs, Tab } from '@mui/material';
import { Chat } from '@mui/icons-material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { DataTable } from '../components/data-table';
import { useGetRecentStreamingThreadsQuery } from '../store/api';
import type { StreamingThreadListItem } from '../store/api';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';

// Workflow configurations - add new workflows here
const WORKFLOWS = [
  { name: 'note_chat', label: 'Note Chat', limit: 20 },
  // Add more workflows as needed:
  // { name: 'another_workflow', label: 'Another Workflow', limit: 20 },
] as const;

export default function StreamingThreadsList() {
  const [selectedWorkflow, setSelectedWorkflow] = useState(0);
  const currentWorkflow = WORKFLOWS[selectedWorkflow];

  const { data, isLoading, error, refetch } = useGetRecentStreamingThreadsQuery({
    limit: currentWorkflow.limit,
    workflowName: currentWorkflow.name,
  });

  const columns = [
    {
      key: 'thread_id' as keyof StreamingThreadListItem,
      label: 'Thread ID',
      render: (value: unknown) => (
        <Typography variant="body2" fontFamily="monospace" fontSize="0.85rem">
          {String(value).substring(0, 12)}...
        </Typography>
      ),
    },
    {
      key: 'user_name' as keyof StreamingThreadListItem,
      label: 'User',
      render: (value: unknown, row: StreamingThreadListItem) => (
        <Typography variant="body2">
          {value ? String(value) : row.user_id?.substring(0, 8) + '...'}
        </Typography>
      ),
    },
    {
      key: 'initial_message' as keyof StreamingThreadListItem,
      label: 'Initial Message',
      render: (value: unknown) => (
        <Typography
          variant="body2"
          sx={{
            maxWidth: 300,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {String(value)}
        </Typography>
      ),
    },
    {
      key: 'event_count' as keyof StreamingThreadListItem,
      label: 'Events',
      render: (value: unknown) => (
        <Typography variant="body2">{String(value)}</Typography>
      ),
    },
    {
      key: 'first_message_at' as keyof StreamingThreadListItem,
      label: 'Started',
      render: (value: unknown) => (
        <Box>
          <Typography variant="body2">{formatRelativeTime(value as string)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDateTime(value as string)}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'last_message_at' as keyof StreamingThreadListItem,
      label: 'Last Activity',
      render: (value: unknown) => (
        <Typography variant="body2">{formatRelativeTime(value as string)}</Typography>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box display="flex" alignItems="center" gap={2}>
            <Chat fontSize="large" color="primary" />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Streaming Threads
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Recent AI agent conversation threads
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Workflow Tabs - only show if multiple workflows exist */}
        {WORKFLOWS.length > 1 && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={selectedWorkflow}
              onChange={(_, newValue: number) => setSelectedWorkflow(newValue)}
              aria-label="workflow tabs"
            >
              {WORKFLOWS.map((workflow, index) => (
                <Tab key={workflow.name} label={workflow.label} id={`workflow-tab-${index}`} />
              ))}
            </Tabs>
          </Box>
        )}

        {error ? (
          <Box textAlign="center" py={8}>
            <Typography color="error" variant="h6" mb={2}>
              Failed to load threads
            </Typography>
            <button
              onClick={() => void refetch()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </Box>
        ) : (
          <DataTable<StreamingThreadListItem>
            title={`${currentWorkflow.label} - Recent Threads (${data?.threads?.length || 0})`}
            columns={columns}
            data={data?.threads || []}
            loading={isLoading}
            getRowUrl={row => `/admin-v2/streaming-thread/${row.thread_id}`}
            emptyMessage="No streaming threads found"
          />
        )}
      </main>
    </div>
  );
}
